import React, { useMemo, useState } from 'react';
import { Checkbox, ContextualMenu, IContextualMenuItem, IContextualMenuProps } from '@fluentui/react';
import { MoreIcon } from '@fluentui/react-icons-mdl2';
import { IconButton } from '@fluentui/react/lib/Button';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { InView } from 'react-intersection-observer';
import { GroupImage } from '../../SharedCommon/UserAvatarName';
import type { IGroupItemView } from '../types';
import { getGroupButtonStyles } from '../shellStyles';
import type { IdigestChatTheme } from '../../theme';
import { formatMessageTime, isMessageExpired } from '../../utils/formatMessageTime';

import { getGroupMessagePreview } from './groupMessagePreview';

import SelectIcon from '../../img/icon-Select.png';
import ManageGroupIcon from '../../img/icon-Group.png';
import DeleteGroupIcon from '../../img/icon-deleteGroup.png';
import ExitGroupIcon from '../../img/icon-ExitGroup.png';
import PinGroupIcon from '../../img/icon-Group-setTop.png';
import UnpinGroupIcon from '../../img/icon-Group-setDown.png';
import UnreadIcon from '../../img/icon-Unread.png';

const menuIcon = (src: string) => ({
  iconProps: {},
  itemProps: { styles: { root: { borderRadius: 10, overflow: 'hidden' as const } } },
  onRenderIcon: () => (
    <img src={src} alt='' style={{ width: 20, height: 20, objectFit: 'contain', flexShrink: 0, margin: '0 4px' }} />
  )
});

const menuSurfaceProps: Pick<IContextualMenuProps, 'styles' | 'calloutProps'> = {
  styles: { root: { borderRadius: 10, overflow: 'hidden' }, list: { padding: 4 } },
  calloutProps: {
    styles: {
      root: { borderRadius: 10 },
      calloutMain: { borderRadius: 10, overflow: 'hidden' }
    }
  }
};

export interface IGroupListItemProps {
  group: IGroupItemView;
  index: number;
  isActive: boolean;
  isSelected: boolean;
  canSelect: boolean;
  isInVisitedGroups: boolean;
  isPendingRead: boolean;
  isTop?: boolean;
  groupDisplayName: (group: IGroupItemView) => string;
  onToggle: (groupId: number, checked: boolean) => void;
  onClick: (group: IGroupItemView) => void;
  selectionMode: boolean;
  onSelect: (group: IGroupItemView) => void;
  onManage: (group: IGroupItemView) => void;
  onHide: (group: IGroupItemView) => void;
  onLeave: (group: IGroupItemView) => void;
  onMarkUnread: (group: IGroupItemView) => void;
  onToggleTop?: (group: IGroupItemView, isCurrentlyTop: boolean) => void;
  /** User-wide hide-messages setting (minutes) from `/user/profile`. */
  userHideMessages?: number;
  t: (key: string, options?: Record<string, unknown>) => string;
  theme?: IdigestChatTheme;
}

/**
 * 群组列表项组件
 * 用于渲染单个群组项，包括复选框、群组信息、新消息提示等
 */
const GroupListItem: React.FC<IGroupListItemProps> = ({
  group,
  index,
  isActive,
  isSelected,
  canSelect,
  isInVisitedGroups,
  isPendingRead,
  isTop = false,
  groupDisplayName,
  onToggle,
  onClick,
  onToggleTop,
  selectionMode,
  onSelect,
  onManage,
  onHide,
  onLeave,
  onMarkUnread,
  userHideMessages,
  t,
  theme = 'light'
}) => {
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | undefined>();

  const handleCheckboxChange = (ev: React.FormEvent<HTMLElement | HTMLInputElement> | undefined, checked?: boolean) => {
    if (ev) {
      ev.stopPropagation();
    }
    if (checked !== undefined) {
      onToggle(group.groupId, checked);
    }
  };

  const handleCheckboxClick = (ev: React.MouseEvent) => {
    ev.stopPropagation();
  };

  const handleCheckboxMouseDown = (ev: React.MouseEvent) => {
    ev.stopPropagation();
  };

  const handleContextMenu = (ev: React.MouseEvent) => {
    if (selectionMode || !onToggleTop) {
      return;
    }
    ev.preventDefault();
    ev.stopPropagation();
    setContextMenu({ x: ev.clientX, y: ev.clientY });
  };

  const displayName = groupDisplayName(group);
  const showMentioned = group.newChatMessage && group.newMentioned && !isInVisitedGroups;
  const showRedDot = (group.newChatMessage && !isInVisitedGroups) || isPendingRead;
  const formattedTime = isMessageExpired(group.lastMessageTime, userHideMessages, group.hideMessages)
    ? ''
    : formatMessageTime(group.lastMessageTime, t);

  const preview = getGroupMessagePreview(group, t);
  const highlightTask =
    showRedDot && (Boolean(group.lastMessageTask) || [6, 8].includes(Number(group.lastMessageData?.type)));

  const buttonStyles = useMemo(() => {
    const base = getGroupButtonStyles(theme);
    const activeBg = 'var(--idigest-chat-surface-hover, #eeeeee)';
    const topBg = activeBg;
    const normalBg = 'var(--idigest-chat-bg, #ffffff)';
    const idleBg = isTop ? topBg : normalBg;
    return {
      ...base,
      flexContainer: {
        ...base.flexContainer,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0
      },
      root: {
        ...base.root,
        height: '42px',
        boxShadow: 'none',
        borderRadius: '999px',
        padding: '2px 41px 2px 5px',
        width: 'auto',
        flex: '1 1 0',
        minWidth: 0,
        maxWidth: '100%',
        cursor: 'pointer',
        backgroundColor: idleBg,
        border: `1px solid ${isTop ? 'transparent' : 'var(--idigest-chat-border, #e5e7eb)'}`
      },
      rootHovered: {
        ...base.rootHovered,
        backgroundColor: activeBg
      },
      rootPressed: {
        ...base.rootPressed,
        backgroundColor: activeBg
      }
    };
  }, [theme, isTop]);

  const contextMenuItems: IContextualMenuItem[] = [
    {
      key: 'select',
      text: t('GroupScreen.Select'),
      ...menuIcon(SelectIcon),
      onClick: () => onSelect(group)
    },
    {
      key: 'manage',
      text: t('ManageMembers'),
      ...menuIcon(ManageGroupIcon),
      disabled: !!(group.isOrgGroup || group.isOneOnOneGroup),
      onClick: () => onManage(group)
    },
    { key: 'hide', text: t('Delete'), ...menuIcon(DeleteGroupIcon), onClick: () => onHide(group) },
    {
      key: 'leave',
      text: t('Exit'),
      ...menuIcon(ExitGroupIcon),
      disabled: !!(group.isOrgGroup || group.isOneOnOneGroup),
      onClick: () => onLeave(group)
    },
    {
      key: isTop ? 'unpin' : 'pin',
      text: t(isTop ? 'GroupScreen.SetDownGroup' : 'GroupScreen.SetTopGroup'),
      ...menuIcon(isTop ? UnpinGroupIcon : PinGroupIcon),
      onClick: () => onToggleTop?.(group, isTop)
    },
    {
      key: 'unread',
      text: t('GroupScreen.PendingRead'),
      ...menuIcon(UnreadIcon),
      onClick: () => onMarkUnread(group)
    }
  ];

  return (
    <InView key={index * 1000}>
      {({ inView, ref }) => (
        <ul
          key={index}
          ref={ref}
          className={`ulGroups group-list-item-row${isTop ? ' group-list-item-row--top' : ''}`}
          onContextMenu={handleContextMenu}
        >
          <div className='group-list-item-inner'>
            <div style={{ position: 'relative', display: 'flex', flex: '1 1 0', minWidth: 0 }}>
              <DefaultButton
                className={`orgGroupButton${isTop ? ' orgGroupButton--top' : ''}${isActive ? ' orgGroupButton--active' : ''}`}
                styles={buttonStyles}
                onClick={() => onClick(group)}
              >
                <span className='group-list-item-avatar'>
                  {inView && (
                    <GroupImage
                      item={group}
                      className={group.isOneOnOneGroup ? 'groupSymbol' : 'groupSymbol groupSymbolBig'}
                    />
                  )}
                  {showRedDot && <span className='group-list-item-unread-dot' />}
                </span>
                <div className='group-list-item-label' title={displayName}>
                  <div className='group-list-item-heading'>
                    <div className='group-list-item-name'>{displayName}</div>
                    {formattedTime && <time className='group-list-item-time'>{formattedTime}</time>}
                  </div>
                  <div className='group-list-item-summary'>
                    <span
                      className={`group-list-item-preview${highlightTask || showMentioned ? ' group-list-item-preview--alert' : ''}`}
                      title={preview}
                    >
                      {showMentioned && `${t('Mentioned')} `}
                      {preview}
                    </span>
                  </div>
                </div>
              </DefaultButton>
              {!selectionMode && (
                <IconButton
                  className='group-list-more-button'
                  ariaLabel={t('GroupScreen.MoreActions')}
                  title={t('GroupScreen.MoreActions')}
                  onRenderIcon={() => <MoreIcon />}
                  menuProps={{ ...menuSurfaceProps, items: contextMenuItems }}
                  styles={{
                    root: {
                      position: 'absolute',
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      right: 6,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--idigest-chat-fg)',
                      background: 'transparent'
                    },
                    menuIcon: { display: 'none' }
                  }}
                />
              )}
            </div>
            {selectionMode && (
              <div className='group-checkbox-container'>
                {canSelect && selectionMode ? (
                  <div onClick={handleCheckboxClick} onMouseDown={handleCheckboxMouseDown}>
                    <Checkbox
                      key={`group-checkbox-${group.groupId}`}
                      checked={isSelected}
                      onChange={handleCheckboxChange}
                      styles={{
                        // Fluent previews the checkmark and darkens the box on :hover/:focus. Mobile browsers keep
                        // :hover after a tap, so an unchecked box would still look checked; show only the real state.
                        root: {
                          WebkitTapHighlightColor: 'transparent',
                          selectors: {
                            ':hover .ms-Checkbox-checkbox, :focus .ms-Checkbox-checkbox, :hover .ms-Checkbox-checkbox:after':
                              {
                                backgroundColor: 'var(--idigest-chat-surface, #ffffff) !important',
                                borderColor: 'var(--idigest-chat-muted, #b0b0b0) !important'
                              },
                            ':hover .ms-Checkbox-checkmark': {
                              opacity: `${isSelected ? 1 : 0} !important`
                            }
                          }
                        },
                        checkbox: {
                          width: '17px',
                          height: '17px',
                          backgroundColor: 'var(--idigest-chat-surface, #ffffff)',
                          borderColor: 'var(--idigest-chat-muted, #b0b0b0)',
                          selectors: {
                            '&:hover': {
                              backgroundColor: 'var(--idigest-chat-surface, #ffffff)',
                              borderColor: 'var(--idigest-chat-muted, #b0b0b0)'
                            }
                          }
                        },
                        checkmark: {
                          color: 'var(--idigest-chat-fg, #171717) !important',
                          fontWeight: 'bold !important'
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div style={{ width: '20px', height: '20px' }} />
                )}
              </div>
            )}
          </div>
          {!selectionMode && contextMenu && onToggleTop && (
            <ContextualMenu
              {...menuSurfaceProps}
              target={contextMenu}
              items={contextMenuItems}
              onDismiss={() => setContextMenu(undefined)}
            />
          )}
        </ul>
      )}
    </InView>
  );
};

export default GroupListItem;
